import { useCallback } from 'react';
import { getModuleLogger } from '../LoggerService';
import type { MouseTouchEvent } from '../tracking/types';
import type { NavigateByMetaData } from './types';
import { useDestination } from './useDestination';
import { usePageNavigation } from './usePageNavigation';
import { usePreventDefaultLinkOut } from './usePreventDefaultLinkOut';
import { useWindowOpen } from './useWindowOpen';

const logger = getModuleLogger(useNavigateTo.name);

/**
 *
 * @param link Only used if it is a string
 * @param internalReference Only used if it is a number
 * @param pageId The id of the page
 * @returns True if it navigated to an external page, false if it navigated to a different page.
 */
export function useNavigateTo(
  link: unknown,
  internalReference: unknown,
  pageId: string | null | undefined,
): NavigateByMetaData {
  const { moveToPage } = usePageNavigation();
  const destination = useDestination(link, internalReference, pageId);
  const { preventDefaultLinkOut } = usePreventDefaultLinkOut();
  const { windowOpen } = useWindowOpen();

  const navigate = useCallback(
    (element: HTMLElement): boolean => {
      const { external, target } = destination;
      preventDefaultLinkOut(element);
      if (!target) {
        logger.warn('No destination could be found');
      } else if (external) {
        windowOpen(target);
      } else {
        moveToPage(target);
      }

      return external;
    },
    [destination, moveToPage, preventDefaultLinkOut, windowOpen],
  );

  const navigateOn = useCallback(
    (event: MouseTouchEvent): boolean => {
      return navigate(event.target as unknown as HTMLElement);
    },
    [navigate],
  );

  return {
    navigate,
    navigateOn,
  };
}
